import React, { useEffect, useState, useRef } from "react";
import { auth, db } from "../../firebase/firebase";
import { doc, getDoc } from "firebase/firestore";
import useFeaturesGate from "../../hooks/useFeaturesGate";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer
} from "recharts";
import "../../styles/BusinessDashboard.css";

export default function BusinessDashboard(){
  const [businessId, setBusinessId] = useState(null);
  const [metrics, setMetrics] = useState({
    totalClients: 0,
    totalLeads: 0,     // keep underlying field for safety
    totalDrivers: 0
  });
  const [loading, setLoading] = useState(true);

  const didLoad = useRef(false);

  useEffect(() => {
    if (didLoad.current) return;

    const unsub = auth.onAuthStateChanged(async (user) => {
      try {
        if (!user) return;

        const userSnap = await getDoc(doc(db, "users", user.uid));
        if (!userSnap.exists()) return;

        const bid = userSnap.data().businessId;
        if (!bid) return;

        setBusinessId(bid);

        const metricsSnap = await getDoc(
          doc(db, "businesses", bid, "metrics", "summary")
        );

        if (metricsSnap.exists()) {
          setMetrics({
            totalClients: metricsSnap.data().totalClients || 0,
            totalLeads: metricsSnap.data().totalLeads || 0, // signals count
            totalDrivers: metricsSnap.data().totalDrivers || 0
          });
        }
      } catch (err) {
        console.warn("Dashboard load skipped:", err.message);
      } finally {
        didLoad.current = true;
        setLoading(false);
      }
    });

    return () => unsub();
  }, []);

  const { loading: featuresLoading } = useFeaturesGate(
    businessId || "disabled"
  );

  if (loading || featuresLoading) {
    return <p className="text-gray-400 p-6">Loading…</p>;
  }

  // keep mock data; rename key for chart label only
  const trend = [
    { day: "Mon", signals: 2 },
    { day: "Tue", signals: 4 },
    { day: "Wed", signals: 3 },
    { day: "Thu", signals: 6 },
    { day: "Fri", signals: 5 }
  ];

  return (
    <>
      <div className="hero-strip">
        <p className="text-muted text-sm">Operational snapshot</p>
        <h2 className="mt-2">{metrics.totalLeads} signals today</h2>
      </div>

      <div className="panel">
        <h3 className="mb-6">Signal Throughput</h3>
        <ResponsiveContainer width="100%" height={260}>
          <LineChart data={trend}>
            <XAxis dataKey="day" />
            <YAxis />
            <Tooltip />
            <Line
              dataKey="signals"
              stroke="#7c3aed"
              strokeWidth={3}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      <div className="panel">
        <h3 className="mb-6">Core Metrics</h3>
        <div className="grid md:grid-cols-3 gap-6">
          <Metric label="Clients" value={metrics.totalClients} />
          <Metric label="Signals" value={metrics.totalLeads} />
          <Metric label="Agents" value={metrics.totalDrivers} />
        </div>
      </div>
    </>
  );
}

function Metric({ label, value }){
  return (
    <div className="card">
      <p className="text-muted text-sm">{label}</p>
      <p className="text-3xl font-bold">{value}</p>
    </div>
  );
}